import { html } from 'lit';
import { ViewBase } from '../view-base.js';

import { baseLogItemViewStyles } from './base-log-item-view-styles.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';

import { q1DocFunctions } from '../../modules/q1-core-functions.js';
import { capFirstChar } from '../../modules/string-helpers-client.js';
import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';

import { enrichProject } from '../../modules/project-helpers-client.js';
import { enrichGuild } from '../../modules/guild-helpers-client.js';
import { enrichLogItem } from '../../modules/log-item-helpers-client.js';
import { enrichMember } from '../../modules/member-helpers-client.js';

/*=== CONSTANTS ===*/

export const ENRICH_SUBJECT = {
  guilds: enrichGuild,
  projects: enrichProject,
};

/*=== CLASS DEFINITION ===*/

/**
 * 
 * # Base Log Item DETAIL View
 * 
 * This is the primary base class for log item views center on a single log item.
 * Example: Log view, log edit view, etc.
 * 
 * The path for these views will contain the id of the log item record.
 * 
 */

export class BaseLogItemDetailView extends ViewBase {
  static styles = [
    ...super.styles,
    baseLogItemViewStyles,
  ];

  static properties = {
    ...super.properties,

    /**
     * Values:
     * - `init`: Starting state, shows page load skeleton
     * - `not-found`: Displays 404-like page
     * - `not-visible`: Displays error message around permissions
     * - `loaded`: Displays loaded view
     * - `load-error`: Displays error message in notice panel. This is different from `this.error`.
     *   The error boolean can be true in any view mode. Load error is specific to errors which
     *   prevent the original load from succeeding.
     */
    viewMode: { type: String },

    logItemId: { type: String },
    logItem: { type: Object },

    subject: { type: Object },
    organizerMembers: { type: Array },
    context: { type: Object },
    authors: { type: Array },
    
    initComplete: { type: Boolean },
    loading: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.viewMode = 'init';

    this.logItemId = null;
    this.logItem = null;

    this.subject = null;
    this.organizerMembers = null;
    this.context = null;
    
    this.initComplete = false;
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
  }

  /*=== LIFECYCLE & INITIALIZATION METHODS ===*/
  
  connectedCallback() {
    super.connectedCallback();

    this._loadLogItemAndSetViewMode();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams')) {
      this._loadLogItemAndSetViewMode();
    }
  }

  /**
   * Overwrite this method to do something after the subject is loaded,
   * but right before the `loading` boolean is set to false.
   * Throw an error to set the view mode to `load-error`.
   * Or just overwrite the view mode to do something else.
   */
  async logItemLoaded() {}

  /** This is meant to be called when we want to refresh the subject if the params have changed. */
  async _loadLogItemAndSetViewMode() {
    const newLogItemId = this.routeParams?.id;
    if ((this.logItemId === newLogItemId) || !newLogItemId) return;

    this.logItemId = newLogItemId;

    return this._refreshLogItem();
  }

  async _refreshLogItem() {
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    
    const { getDoc } = q1DocFunctions;
    
    try {
      const response = await getDoc({
        docCollection: 'logItems',
        docId: this.logItemId,
      });
      const responseData = response.data;
      const { docData: logItem, subject, currentMemberContext, organizers, authors } = responseData;
      
      this.logItem = enrichLogItem(logItem);
      this.subject = ENRICH_SUBJECT[logItem.subjectCollection]
        ? ENRICH_SUBJECT[logItem.subjectCollection](subject)
        : subject;
      this.organizerMembers = organizers.map((organizer) => enrichMember(organizer));
      this.context = currentMemberContext;
      this.authors = authors.map((author) => enrichMember(author));

      this._setThemeStyles();

      this.setAfLink_db('logItems', this.logItemId);

      // Call the logItemLoaded method to allow the child page to set title & insert custom logic
      await this.logItemLoaded();

      this.viewMode = 'loaded';
    } catch (error) {
      console.error('Error Loading Log Item:', error);

      switch (error.details?.resultCode) {
        case 'not-found':
          this.setErrorViewMeta('Log Item Not Found', 'There is no log item with that ID.');
          this.viewMode = 'not-found';
          break;

        case 'view-not-allowed':
          this.setErrorViewMeta('Private Log Item', 'This log item is not visible to you.');
          this.viewMode = 'not-visible';
          break;

        default:
          this.setErrorViewMeta(
            'Log Item Load Error',
            error.message || 'Something went wrong while loading this log item.'
          );
          this.error = true;
          this.errorMessage = error.message;
          this.viewMode = 'load-error';
          break;
      }
    } finally {
      this.initComplete = true;
      this.loading = false;
    }
  }

  /** Call this to set the css styles from source object */
  _setThemeStyles(sourceObject = this.subject, targetElement = this) {
    if (!sourceObject) return;

    targetElement.style.setProperty('--theme-color-primary', sourceObject.colorPrimary);
    targetElement.style.setProperty('--theme-color-secondary', sourceObject.colorSecondary);
    targetElement.style
      .setProperty('--theme-text-color-primary', sourceObject.textColorPrimary_hex);
    targetElement.style
      .setProperty('--theme-text-color-secondary', sourceObject.textColorSecondary_hex);
  }

  /*=== ACTIONS & EVENT HANDLERS ===*/

  // NONE YET
  
  /*=== GETTERS ===*/

  /** Overwriting this from view-base to conditionally set white text on q1-app-header. */
  get appHeaderVariant() {
    // NOTE: The very top of the view content is the fully saturated primary color, so this setting
    // tells us whether we need white text to contrast with that.
    return this.subject?.textColorPrimary_isWhite ? 'white-text' : 'default';
  }

  get subjectId() {
    return this.subject?.id;
  }

  get isDraft() {
    return this.logItem?.status === 'draft';
  }

  /*=== RENDER TEMPLATES ===*/

  get contentTemplate() {
    return html`
      ${this.contentHeaderTemplate}

      ${this.viewModeTemplate}

      <q1-spinner-panel label="Loading..." ?open=${!this.initComplete}></q1-spinner-panel>
    `;
  }

  /**
   * Overwrite with contents of header once init is complete (for all view modes).
   * It should render something even if `this.subject` is blank.
   */
  get contentHeaderTemplate() {}

  /** Overwrite with contents of loaded body. */
  get viewModeTemplate_loaded() {}

  get viewModeTemplate() {
    switch (this.viewMode) {
      case 'init':
        return this.viewModeTemplate_init;

      case 'not-found':
        return this.viewModeTemplate_notFound;

      case 'not-visible':
        return this.viewModeTemplate_notVisible;

      case 'load-error':
        return this.viewModeTemplate_loadError;
        
      case 'loaded':
        return this.viewModeTemplate_loaded;

      default:
        this.errorMessage = `Unknown view mode: ${this.viewMode}`;
        this.error = true;
        return this.viewModeTemplate_loadError;
    }
  }

  get viewModeTemplate_init() {
    // IMPROVEME: Add skeleton loading view
    return null;
  }

  get viewModeTemplate_notFound() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="block"></sl-icon>

          <h2>Record not found...</h2>

          <p>
            Please check the URL and try again.
          </p>
        </div>
      </notice-panel>
    `;
  }

  get viewModeTemplate_notVisible() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="visibility_off"></sl-icon>

          <h2>Page not visible to you</h2>

          <p>
            Please check the URL and try again.
            Reach out to the foundation team if you have any questions.
          </p>
        </div>
      </notice-panel>
    `;
  }
  
  get viewModeTemplate_loadError() {
    return html`
      <notice-panel class="hero danger">
        <div class="inner">
          <sl-icon library="material" name="error"></sl-icon>

          <h2>An error occurred!</h2>

          <p>
            ${this.errorMessage}
          </p>
        </div>
      </notice-panel>
    `;
  }
  
}
